/* BulkImageUploadModal — drop a ZIP, a Google Drive / Dropbox folder
 * URL, or a multi-page PDF catalog and we attach images to products by
 * SKU. Three input modes share one results panel. */
import { useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  Upload, FolderArchive, CheckCircle2, AlertCircle, Loader2, ImageIcon,
  Cloud, FileText, Link2, Sparkles,
} from "lucide-react";

const MODES = [
  { id: "zip",    label: "ZIP file",         icon: FolderArchive,
    blurb: "Drop a ZIP of images named by SKU." },
  { id: "folder", label: "Drive / Dropbox",  icon: Cloud,
    blurb: "Paste a public folder URL — we'll fetch every image inside." },
  { id: "pdf",    label: "PDF catalog",      icon: FileText,
    blurb: "Upload a printable PDF — we extract every embedded image and match to SKUs." },
];

export default function BulkImageUploadModal({ onClose }) {
  const fileRef = useRef(null);
  const pdfRef  = useRef(null);
  const [mode, setMode]       = useState("zip");
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [folderUrl, setFolderUrl] = useState("");

  const post = async (path, fd) => {
    setBusy(true); setResult(null); setErrorMsg(null);
    try {
      const r = await api.post(path, fd, fd instanceof FormData
        ? { headers: { "Content-Type": "multipart/form-data" } }
        : {});
      setResult(r.data);
      const m = r.data?.matched_skus || 0;
      if (m > 0) toast.success(`${m} product${m !== 1 ? "s" : ""} updated`);
      else toast.warning("Upload finished but no SKUs matched. See report.");
    } catch (err) {
      setErrorMsg(err.response?.data?.detail?.message || err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const uploadZip = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", f);
    await post("/seller/catalog-import/upload-images", fd);
  };

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", f);
    await post("/seller/catalog-import/import-pdf", fd);
  };

  const submitFolder = async () => {
    if (!folderUrl) { toast.error("Paste a folder URL"); return; }
    await post("/seller/catalog-import/import-folder", { url: folderUrl });
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="bulk-image-upload-modal">
        <DialogHeader>
          <DialogTitle className="display text-2xl tracking-tight inline-flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-foreground/55"/>Bulk attach images
          </DialogTitle>
          <DialogDescription>
            Three ways to upload images at once. We match each image to a product by SKU.
          </DialogDescription>
        </DialogHeader>

        {/* Mode picker */}
        <div className="grid grid-cols-3 gap-2" data-testid="mode-picker">
          {MODES.map(m => (
            <button key={m.id} onClick={() => { setMode(m.id); setResult(null); setErrorMsg(null); }}
              data-testid={`mode-${m.id}`}
              className={`rounded-xl border p-3 text-left transition-colors ${
                mode === m.id
                  ? "bg-foreground text-background border-foreground"
                  : "bg-white border-border hover:border-foreground/40"
              }`}>
              <m.icon className={`w-4 h-4 mb-1.5 ${mode === m.id ? "text-background" : "text-foreground/55"}`}/>
              <div className="text-[12px] font-medium">{m.label}</div>
              <div className={`text-[10.5px] mt-0.5 leading-snug ${mode === m.id ? "text-background/70" : "text-foreground/55"}`}>{m.blurb}</div>
            </button>
          ))}
        </div>

        {!result && !errorMsg && (
          <>
            {/* ── ZIP ─────────────────────────────────────────────── */}
            {mode === "zip" && (
              <>
                <div className="rounded-xl bg-secondary/50 border border-border p-4 text-[12px] space-y-2 leading-relaxed">
                  <div className="font-medium text-foreground">Naming conventions we accept:</div>
                  <ul className="space-y-1 text-foreground/70">
                    <li>· <span className="mono">BRD-001.jpg</span> → primary image for that SKU</li>
                    <li>· <span className="mono">BRD-001-2.jpg</span> → 2nd gallery image</li>
                    <li>· <span className="mono">BRD-001/</span> folder → every file inside, in order</li>
                  </ul>
                </div>
                <Button onClick={() => fileRef.current?.click()} disabled={busy}
                  data-testid="zip-upload-btn"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 w-full mt-3">
                  {busy ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin"/>Uploading…</> :
                          <><Upload className="w-3.5 h-3.5 mr-2"/>Choose ZIP file</>}
                </Button>
                <input ref={fileRef} type="file" accept=".zip,application/zip" onChange={uploadZip} className="hidden"/>
              </>
            )}

            {/* ── Folder ─────────────────────────────────────────── */}
            {mode === "folder" && (
              <>
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11.5px] text-amber-900 leading-relaxed">
                  <AlertCircle className="inline w-3.5 h-3.5 mr-1"/>
                  Make sure your folder is shared <strong>publicly</strong> (anyone with the link can view). We don&apos;t store credentials.
                </div>
                <div className="mt-3">
                  <label className="text-[11px] font-medium text-foreground/65">Folder URL</label>
                  <Input value={folderUrl} onChange={(e) => setFolderUrl(e.target.value)}
                    data-testid="folder-url-input"
                    className="mt-1.5 mono text-[11.5px]"
                    placeholder="https://drive.google.com/drive/folders/… or https://www.dropbox.com/sh/…"/>
                </div>
                <Button onClick={submitFolder} disabled={busy || !folderUrl}
                  data-testid="folder-submit-btn"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 w-full mt-3">
                  {busy ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin"/>Fetching folder…</> :
                          <><Link2 className="w-3.5 h-3.5 mr-2"/>Ingest folder</>}
                </Button>
                <div className="mt-3 text-[10.5px] text-foreground/50 leading-relaxed">
                  Same naming rules as ZIP — file names like <span className="mono">BRD-001.jpg</span> get matched automatically.
                </div>
              </>
            )}

            {/* ── PDF ────────────────────────────────────────────── */}
            {mode === "pdf" && (
              <>
                <div className="rounded-xl bg-secondary/50 border border-border p-4 text-[12px] space-y-2 leading-relaxed">
                  <div className="font-medium text-foreground inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5"/>How this works
                  </div>
                  <ul className="space-y-1 text-foreground/70">
                    <li>· Upload a multi-page PDF catalog (one product per page works best)</li>
                    <li>· We extract every embedded image with PyMuPDF</li>
                    <li>· We read the text on each page and match each image to a SKU you already have</li>
                    <li>· Anything we can&apos;t match is returned for hand-assign</li>
                  </ul>
                </div>
                <Button onClick={() => pdfRef.current?.click()} disabled={busy}
                  data-testid="pdf-upload-btn"
                  className="rounded-full bg-foreground text-background hover:bg-foreground/90 h-11 w-full mt-3">
                  {busy ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin"/>Extracting…</> :
                          <><FileText className="w-3.5 h-3.5 mr-2"/>Choose PDF catalog</>}
                </Button>
                <input ref={pdfRef} type="file" accept="application/pdf,.pdf" onChange={uploadPdf} className="hidden"/>
              </>
            )}
          </>
        )}

        {/* ── Result / error ───────────────────────────────────── */}
        {errorMsg && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-[12.5px] text-rose-900" data-testid="image-import-error">
            <AlertCircle className="inline w-4 h-4 mr-1.5"/>{errorMsg}
          </div>
        )}

        {result && (
          <ResultPanel result={result} mode={mode} onClose={onClose}/>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResultPanel({ result, mode, onClose }) {
  return (
    <div className="space-y-3" data-testid="image-import-result">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12.5px] text-emerald-900">
        <CheckCircle2 className="inline w-4 h-4 mr-1.5"/>
        <strong>{result.matched_skus || 0}</strong> product{(result.matched_skus || 0) !== 1 ? "s" : ""} updated · <strong>{result.updated || 0}</strong> image{(result.updated || 0) !== 1 ? "s" : ""} attached
        {mode === "pdf" && (
          <>
            {" · "}<strong>{result.pages}</strong> page{result.pages !== 1 ? "s" : ""} scanned
            {result.total_images > 0 && <> · <strong>{result.total_images}</strong> images extracted</>}
          </>
        )}
        {mode === "folder" && result.provider && (
          <>{" · "}<strong>{result.provider}</strong> · {result.downloaded || 0} of {result.total_files || 0} downloaded</>
        )}
      </div>

      {(result.unmatched_images?.length || 0) > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] text-amber-900" data-testid="image-import-unmatched">
          <AlertCircle className="inline w-4 h-4 mr-1.5"/>
          {result.unmatched_images.length} image{result.unmatched_images.length !== 1 ? "s" : ""} couldn&apos;t be matched to a SKU.
          <div className="mt-2 grid grid-cols-4 gap-2">
            {result.unmatched_images.slice(0, 8).map((img, i) => (
              <div key={i} className="rounded-lg overflow-hidden border border-amber-200 bg-white text-center">
                <img src={img.url} alt={`page ${img.page}`} className="w-full h-20 object-cover"/>
                <div className="text-[9.5px] text-foreground/55 py-1">p.{img.page}</div>
              </div>
            ))}
          </div>
          {result.unmatched_images.length > 8 && (
            <div className="mt-1.5 text-[10.5px] text-foreground/55">+ {result.unmatched_images.length - 8} more (uploaded to your library, assign from Catalog &gt; Edit product).</div>
          )}
        </div>
      )}

      {(result.skipped?.length || 0) > 0 && (
        <details className="rounded-xl border border-border bg-secondary/30 px-4 py-2 text-[11.5px]">
          <summary className="cursor-pointer">Skipped: {result.skipped.length} file{result.skipped.length !== 1 ? "s" : ""}</summary>
          <ul className="mt-2 space-y-1 text-foreground/65">
            {result.skipped.slice(0, 10).map((s, i) => (
              <li key={i}>· {s.filename} — <span className="text-rose-700">{s.reason}</span></li>
            ))}
          </ul>
        </details>
      )}

      {(result.warnings?.length || 0) > 0 && (
        <ul className="text-[11px] text-amber-700 space-y-0.5">
          {result.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
        </ul>
      )}

      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={onClose} className="rounded-full flex-1">Close</Button>
        <Button onClick={() => window.location.reload()}
          className="rounded-full flex-1 bg-foreground text-background hover:bg-foreground/90">View catalog</Button>
      </div>
    </div>
  );
}
